import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import columnsDef from './columnsDef';
import { startOfDay, subDays } from 'date-fns';
import moment from 'moment';

import type { CostsWithVehicle, DateRange } from '@cobliteam/common-types';

import {
  isDeviceTypeDashcam,
  isWebDataFetching,
  isWebDataSuccess,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';
import ReportDataProblemModalButton from '@cobliteam/reports/src/components/ReportDataProblemModal/ReportDataProblemModalButton';
import ReportDataProblemModalTrigger from '@cobliteam/reports/src/components/ReportDataProblemModal/ReportDataProblemModalTrigger';

import type { AppState } from '@cobliteam/store';

import { useVehicle } from '@cobliteam/hooks';

import { DateUtils, Heading1, usePrevious } from '@cobliteam/base-components';
import withPermission from '@cobliteam/base-components/src/PermissionsGate/PermissionsGate';

import { Button, DataTable, DatePicker, Page } from '@cobliteam/ui/components';

import { SaveAltRounded } from '@material-ui/icons';

import {
  downloadCostsRequest,
  loadCostsRequest,
} from '../../store/Costs/actionCreators';
import { selectVehiclesCostsInfo } from '../../store/Costs/selectors';
import CostsPermissions from '../permissions';

const defaultPeriod = () => {
  const today = new Date();
  const start = startOfDay(subDays(today, 7)).getTime();
  const end = new Date().getTime();

  return { start, end };
};

const CostsPage = () => {
  const [period, setPeriod] = React.useState<DateRange>(defaultPeriod());

  const dispatch = useDispatch();

  const { getVehicles } = useVehicle();
  const { data: vehiclesWebData, refetch: refetchVehicles } = getVehicles({});

  const costsWebData = useSelector((state: AppState) =>
    selectVehiclesCostsInfo(state)
  );
  const isLoadingReport = isWebDataFetching(
    useSelector((state: AppState) => state.costs.costsReportWebData)
  );
  const hasExportedReport =
    usePrevious(isLoadingReport) === true && isLoadingReport === false;
  const isLoadingExportCosts = isWebDataFetching(
    useSelector((state: AppState) => state.costs.costsReportWebData)
  );

  const fetchCostsData = () => {
    dispatch(loadCostsRequest(period));
    refetchVehicles();
  };

  const exportCosts = () => {
    dispatch(downloadCostsRequest(period));
  };

  React.useEffect(() => fetchCostsData(), []);

  const handlePeriodChange = (newPeriod: DateRange) => {
    const daysRange = moment(newPeriod.end).diff(
      moment(newPeriod.start),
      'days'
    );
    Analytics.track('Costs Period Selection Changed', { range: daysRange });
    setPeriod(newPeriod);
    dispatch(loadCostsRequest(newPeriod));
  };

  const onClickExport = () => {
    Analytics.track('Exported Costs Report');
    exportCosts();
  };

  const wantedCostswithVehicleData = () => {
    if (isWebDataSuccess(costsWebData)) {
      const vehicles = vehiclesWebData;
      const allCosts = costsWebData.data;

      const costswithVehicle = allCosts.map((cost) => ({
        ...cost,
        vehicle: vehicles?.find((v) => v.id === cost.vehicleId)!,
        withDashCam: isDeviceTypeDashcam(
          vehicles?.find((v) => v.id === cost.vehicleId)?.deviceType as string
        ),
      }));

      return costswithVehicle;
    }
  };

  return (
    <Page>
      <Page.Header>
        <Page.HeaderTitle>
          <Heading1>Gastos</Heading1>
          <ReportDataProblemModalButton />
        </Page.HeaderTitle>
      </Page.Header>
      <Page.Body>
        <DataTable<CostsWithVehicle, unknown>
          data={wantedCostswithVehicleData()}
          columnsDef={columnsDef}
          isLoading={!isWebDataSuccess(costsWebData)}
          onRefresh={fetchCostsData}
          datePicker={
            <DatePicker
              startDate={new Date(period.start)}
              endDate={new Date(period.end)}
              maxDate={new Date()}
              onChange={(range) =>
                handlePeriodChange({
                  start: DateUtils.getStartOfDayInMillis(range[0]),
                  end: DateUtils.getEndOfDayInMillis(range[1]),
                })
              }
            />
          }
          export={() => (
            <Button
              variant='outlined'
              color='secondary'
              size='condensed'
              onClick={onClickExport}
              startIcon={<SaveAltRounded />}
              loading={isLoadingExportCosts}
              disabled={!isWebDataSuccess(costsWebData)}
            >
              Exportar
            </Button>
          )}
        />
      </Page.Body>
      <ReportDataProblemModalTrigger showIfAvailable={hasExportedReport} />
    </Page>
  );
};

export default withPermission(CostsPermissions.VIEW_FLEET_EXPENSES, CostsPage);
